import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../../hooks/usePermission';
import { UserType } from '../../interfaces/userType.enum';
import { Shift } from '../../interfaces/shift/Shift';
import { RosterCell } from '../../interfaces/shift/RosterCell';
import { ShiftRosterService } from '../../services/shift-roster.service';
import { ShiftService } from '../../services/shift.service';
import { UserService } from '../../services/user.service';
import RosterCellOverrideDialog from './RosterCellOverrideDialog';
import BulkSwapDialog from './BulkSwapDialog';
import { isInstanceStatus } from './instanceStatus';

interface RowUser {
    id: string;
    username: string;
    name?: string;
}

const PAGE_SIZE = 50;
const MAX_DAYS = 31;

const todayStr = () => new Date().toISOString().slice(0, 10);
const addDays = (dateStr: string, n: number): string => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
};

// Generate array of date strings between from and to (inclusive)
const buildDateRange = (from: string, to: string): string[] => {
    const dates: string[] = [];
    let cur = from;
    while (cur <= to) {
        dates.push(cur);
        cur = addDays(cur, 1);
    }
    return dates;
};

// Day-of-week index (0 = Sunday), used for the short weekday label
const dayIndex = (dateStr: string) =>
    new Date(dateStr + 'T00:00:00').getDay() as 0 | 1 | 2 | 3 | 4 | 5 | 6;

type StatusColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const statusChipColor = (status: string): StatusColor => {
    switch (status) {
        case 'SCHEDULED':  return 'default';
        case 'WORKED':     return 'success';
        case 'ABSENT':     return 'error';
        case 'ON_LEAVE':   return 'warning';
        case 'HOLIDAY':    return 'info';
        case 'CANCELLED':  return 'secondary';
        default:           return 'default';
    }
};

const ShiftRosterCalendar: React.FC = () => {
    const { user, selectedInstitutionId } = useAuth();
    const toast = useToast();
    const { t } = useTranslation(['shifts', 'common']);
    const statusLabel = (status: string) => (isInstanceStatus(status) ? t(`instanceStatus.${status}`) : status);

    const isAdmin = user?.roles?.includes(UserType.InstitutionAdmin) ?? false;
    const canView       = usePermission('SHIFT_VIEW', isAdmin, ['SHIFT_VIEW_ALL']);
    const canOverride   = usePermission('SHIFT_OVERRIDE_INSTANCE', isAdmin);
    const canRegenerate = usePermission('SHIFT_VIEW_ALL', isAdmin);

    const [fromDate, setFromDate] = useState(todayStr());
    const [toDate, setToDate]     = useState(addDays(todayStr(), 6));
    const [shiftFilter, setShiftFilter] = useState('');
    const [userSearch, setUserSearch]   = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const [shifts, setShifts]       = useState<Shift[]>([]);
    const [users, setUsers]         = useState<RowUser[]>([]);
    const [rosterCells, setRosterCells] = useState<RosterCell[]>([]);
    const [loading, setLoading]     = useState(false);
    const [rangeError, setRangeError] = useState('');

    const [overrideCell, setOverrideCell]   = useState<RosterCell | null>(null);
    const [overrideOpen, setOverrideOpen]   = useState(false);
    const [bulkSwapOpen, setBulkSwapOpen]   = useState(false);

    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const handleSearchChange = (val: string) => {
        setUserSearch(val);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setDebouncedSearch(val);
            setPage(0);
        }, 400);
    };

    // Column array derived from date range
    const dateColumns = useMemo(() => {
        if (!fromDate || !toDate || fromDate > toDate) return [];
        const diff = (new Date(toDate).getTime() - new Date(fromDate).getTime()) / 86400000;
        if (diff > MAX_DAYS) return [];
        return buildDateRange(fromDate, toDate);
    }, [fromDate, toDate]);

    // Validate range
    const validateRange = useCallback((): boolean => {
        if (!fromDate || !toDate) { setRangeError(t('roster.bothDatesRequired')); return false; }
        if (fromDate > toDate)    { setRangeError(t('roster.fromAfterTo')); return false; }
        const diff = (new Date(toDate).getTime() - new Date(fromDate).getTime()) / 86400000;
        // diff > 31 means 32+ days (more than 31-day window)
        if (diff > MAX_DAYS) { setRangeError(t('roster.rangeTooLong', { max: MAX_DAYS })); return false; }
        setRangeError('');
        return true;
    }, [fromDate, toDate, t]);

    const fetchData = useCallback(async () => {
        if (!selectedInstitutionId) return;
        if (!validateRange()) return;
        setLoading(true);
        try {
            const [pageData, allShifts, cells] = await Promise.all([
                UserService.getInstitutionUsers(page, PAGE_SIZE, selectedInstitutionId, debouncedSearch || undefined),
                ShiftService.list(selectedInstitutionId),
                ShiftRosterService.getRoster(selectedInstitutionId, fromDate, toDate, undefined, shiftFilter || undefined),
            ]);
            const content: RowUser[] = (pageData.content || []).map((u: any) => ({
                id: u.id,
                username: u.username,
                name: u.name || undefined,
            }));
            setUsers(content);
            setTotalElements(pageData.totalElements || 0);
            setShifts(allShifts);
            setRosterCells(cells);
        } catch (err) {
            toast.showApiError(err, t('roster.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [selectedInstitutionId, page, debouncedSearch, fromDate, toDate, shiftFilter, validateRange, toast]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchData]);

    // O(1) cell lookup: Map<userId, Map<businessDate, RosterCell>>
    const cellMap = useMemo(() => {
        const m = new Map<string, Map<string, RosterCell>>();
        rosterCells.forEach(cell => {
            if (!m.has(cell.userId)) m.set(cell.userId, new Map());
            m.get(cell.userId)!.set(cell.businessDate, cell);
        });
        return m;
    }, [rosterCells]);

    const handleRegenerate = async () => {
        if (!selectedInstitutionId || !validateRange()) return;
        try {
            const result = await ShiftRosterService.regenerate(selectedInstitutionId, fromDate, toDate);
            toast.showSuccess(t('roster.regenerated', { count: result.generated }));
            await fetchData();
        } catch (err) {
            toast.showApiError(err, t('roster.regenerateFailed'));
        }
    };

    const handleCellClick = (cell: RosterCell | undefined) => {
        if (!cell || !canOverride) return;
        setOverrideCell(cell);
        setOverrideOpen(true);
    };

    const handleOverrideSaved = () => {
        setOverrideOpen(false);
        setOverrideCell(null);
        fetchData();
    };

    const handleBulkSwapSaved = () => {
        setBulkSwapOpen(false);
        fetchData();
    };

    if (!canView) {
        return <Typography sx={{ p: 3 }}>{t('roster.noPermission')}</Typography>;
    }

    return (
        <Box sx={{ padding: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{t('roster.title')}</Typography>
                <Stack direction="row" spacing={1}>
                    {canOverride && (
                        <Button
                            variant="outlined"
                            onClick={() => setBulkSwapOpen(true)}
                            data-testid="bulk-swap-btn"
                        >
                            {t('roster.bulkSwap')}
                        </Button>
                    )}
                    {canRegenerate && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleRegenerate}
                            data-testid="regenerate-btn"
                        >
                            {t('roster.regenerate')}
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Filters */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
                <TextField
                    label={t('common:fields.from')}
                    type="date"
                    size="small"
                    value={fromDate}
                    onChange={e => { setFromDate(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'data-testid': 'filter-from' }}
                    sx={{ width: 180 }}
                />
                <TextField
                    label={t('common:fields.to')}
                    type="date"
                    size="small"
                    value={toDate}
                    onChange={e => { setToDate(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'data-testid': 'filter-to' }}
                    sx={{ width: 180 }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="shift-filter-label">{t('fields.shift')}</InputLabel>
                    <Select
                        labelId="shift-filter-label"
                        value={shiftFilter}
                        label={t('fields.shift')}
                        onChange={e => { setShiftFilter(e.target.value); setPage(0); }}
                        inputProps={{ 'data-testid': 'filter-shift' }}
                    >
                        <MenuItem value="">{t('allShifts')}</MenuItem>
                        {shifts.map(s => (
                            <MenuItem key={s.id} value={s.id}>{s.name} ({s.code})</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label={t('roster.searchUser')}
                    size="small"
                    value={userSearch}
                    onChange={e => handleSearchChange(e.target.value)}
                    inputProps={{ 'data-testid': 'filter-user-search' }}
                    sx={{ width: 220 }}
                />
            </Stack>

            {rangeError && (
                <Alert severity="error" sx={{ mb: 2 }} data-testid="range-error">
                    {rangeError}
                </Alert>
            )}

            {/* Grid */}
            {loading ? (
                <Typography>{t('loading')}</Typography>
            ) : (
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 160, fontWeight: 600, position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                                    {t('roster.userColumn')}
                                </TableCell>
                                {dateColumns.map(d => (
                                    <TableCell key={d} align="center" sx={{ minWidth: 90, fontWeight: 600 }}>
                                        <Box>{d.slice(5)}</Box>
                                        <Box sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{t(`common:weekdaysShort.${dayIndex(d)}`)}</Box>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={dateColumns.length + 1}>{t('noUsers')}</TableCell>
                                </TableRow>
                            ) : (
                                users.map(u => (
                                    <TableRow key={u.id} data-testid={`roster-row-${u.id}`}>
                                        <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                                            {u.name || u.username}
                                        </TableCell>
                                        {dateColumns.map(d => {
                                            const cell = cellMap.get(u.id)?.get(d);
                                            return (
                                                <TableCell
                                                    key={d}
                                                    align="center"
                                                    sx={{ cursor: cell && canOverride ? 'pointer' : 'default', p: 0.5 }}
                                                    onClick={() => handleCellClick(cell)}
                                                    data-testid={`roster-cell-${u.id}-${d}`}
                                                >
                                                    {cell ? (
                                                        <Tooltip title={cell.isManualOverride
                                                            ? t('roster.cellTooltipOverride', { status: statusLabel(cell.status) })
                                                            : statusLabel(cell.status)}>
                                                            <Chip
                                                                label={`${cell.shiftCode ?? '?'}${cell.isManualOverride ? ' ★' : ''}`}
                                                                color={statusChipColor(cell.status)}
                                                                size="small"
                                                            />
                                                        </Tooltip>
                                                    ) : (
                                                        <Typography variant="body2" color="text.disabled">—</Typography>
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalElements}
                        page={page}
                        onPageChange={(_e, newPage) => setPage(newPage)}
                        rowsPerPage={PAGE_SIZE}
                        rowsPerPageOptions={[PAGE_SIZE]}
                    />
                </TableContainer>
            )}

            {/* Override dialog */}
            {overrideCell && selectedInstitutionId && (
                <RosterCellOverrideDialog
                    open={overrideOpen}
                    onClose={() => { setOverrideOpen(false); setOverrideCell(null); }}
                    cell={overrideCell}
                    institutionId={selectedInstitutionId}
                    shifts={shifts}
                    onSaved={handleOverrideSaved}
                />
            )}

            {/* Bulk swap dialog */}
            {selectedInstitutionId && (
                <BulkSwapDialog
                    open={bulkSwapOpen}
                    onClose={() => setBulkSwapOpen(false)}
                    institutionId={selectedInstitutionId}
                    users={users}
                    onSaved={handleBulkSwapSaved}
                />
            )}
        </Box>
    );
};

export default ShiftRosterCalendar;
